import TransactionService from '../../../../services/transaction.service';
import { validateAttachmentFile } from '../validations';
import { ATTACHMENT } from '../../../../utils/appConstant';
import { Attachment } from '../../../../interfaces/transaction.interface';

export interface ChunkedUploadCallbacks {
  onProgress?: (uploadedChunks: number, totalChunks: number) => void;
  onComplete?: (attachment: Attachment) => void;
  onError?: (error: any) => void;
}

/**
 * Drives a single file's upload lifecycle: init -> chunk loop -> finalize.
 * A failed chunk is retried (bounded) before surfacing onError — earlier,
 * already-uploaded chunks are never re-sent (AC5).
 */
export const chunkedUpload = async (
  file: File,
  transactionId: string,
  { onProgress, onComplete, onError }: ChunkedUploadCallbacks = {}
): Promise<Attachment | undefined> => {
  const validation = validateAttachmentFile(file);
  if (!validation.valid) {
    onError && onError(new Error(validation.error));
    return undefined;
  }

  const totalChunks = Math.max(1, Math.ceil(file.size / ATTACHMENT.chunkSizeBytes));

  let sessionId: string;
  try {
    const initResponse: any = await TransactionService.initAttachmentUpload(transactionId, {
      filename: file.name,
      sizeBytes: file.size,
      mimeType: file.type,
      totalChunks,
    });
    sessionId = initResponse.sessionId;
  } catch (error) {
    onError && onError(error);
    return undefined;
  }

  for (let index = 0; index < totalChunks; index++) {
    const start = index * ATTACHMENT.chunkSizeBytes;
    const end = Math.min(start + ATTACHMENT.chunkSizeBytes, file.size);
    const chunk = file.slice(start, end);

    let attempt = 0;
    let uploaded = false;
    let lastError: any;

    while (attempt < ATTACHMENT.maxRetriesPerChunk && !uploaded) {
      try {
        await TransactionService.uploadAttachmentChunk(transactionId, sessionId, index, chunk);
        uploaded = true;
      } catch (error) {
        attempt++;
        lastError = error;
      }
    }

    if (!uploaded) {
      onError && onError(lastError);
      return undefined;
    }

    onProgress && onProgress(index + 1, totalChunks);
  }

  try {
    const attachment: any = await TransactionService.finalizeAttachmentUpload(transactionId, sessionId);
    onComplete && onComplete(attachment);
    return attachment;
  } catch (error) {
    onError && onError(error);
    return undefined;
  }
};
